/*global navigator*/
/* eslint-disable global-require, prefer-const, no-param-reassign */

import {action, computed, observable} from 'mobx';
import {suspectInfiniteLoop} from 'spa/debug';

import {async} from 'spa/server-wait';

const isServer = typeof window === 'undefined';
let loadConfigFile;

/*
 * Whether we are in the webpack build context or not so we can load the config in the correct context
 */
if (__WEBPACK__) {
  loadConfigFile = config =>
    new Promise(resolve =>
      require.ensure([], () => {
        resolve(require(`../../../../../config/${config}.json`));
      }));
} else {
  loadConfigFile = config =>
    Promise.resolve(require(`../../../../../config/${config}.json`));
}

class Config {
  @observable config;
  @observable lastCountry
  @observable lastCity

  _pendingConfig = null;
  @async
  @action('loadConfig - config context')
  @suspectInfiniteLoop
  loadConfig(env) {
    // Prevent double loading
    if (this._pendingConfig === env) return null;

    this._pendingConfig = env;
    return loadConfigFile(env)
      .then(action('loaded config', (response) => {
        this.config = response;
        this._pendingConfig = null;
      }));
  }

  // Return the full config
  get FULL_CONFIG() {
    return this.config;
  }

  /*
   * Returns the current config matching the current i18n region context and city id is null
   */
  @computed get currentContextConfig() {
    const {config} = this;
    const {i18n} = this.ctx;
    if (i18n.umRegion && this.config) {
      let [regionConf] = config.filter(conf => (conf.country_id === i18n.umRegion && conf.city_id === null));
      if (regionConf) {
        return regionConf.keyValues;
      }
    }

    // If there's no region set, return UK as default
    return config.filter(conf => (conf.country_id === 'UK'))[0].keyValues;
  }

  /*
   * Returns the current config matching the current last country passed to the config
   * and city id is null
   */
  @computed get latestCountryConfig() {
    const {i18n} = this.ctx;
    const {config, lastCountry} = this;

    if (i18n.umRegion && this.config) {
      let [regionConf] = config.filter(conf => (conf.country_id === lastCountry && conf.city_id === null));
      if (regionConf) {
        return regionConf.keyValues;
      }
    }

    // If there's no region set, return UK as default
    return config.filter(conf => (conf.country_id === 'UK'))[0].keyValues;
  }

  /*
   * Returns the current config matching the current last country passed to the config
   * and the last city passed to the config
   */
  @computed get latestCityConfig() {
    const {i18n} = this.ctx;
    const {config, lastCity, lastCountry} = this;

    if (i18n.umRegion && this.config) {
      let [regionConf] = config.filter(conf => (conf.country_id === lastCountry && conf.city_id === lastCity));
      if (regionConf) {
        return regionConf.keyValues;
      }
    }

    return null;
  }

  /*
   * Used to pull a specific value from the config. By default it will load the current regions keys
   */
  getValue(key, filterCountry, filterCity) {
    const {currentContextConfig} = this;

    if (filterCountry && !filterCity) {
      this.lastCountry = filterCountry;

      const value = this.latestCountryConfig[key];
      if (__DEV__) {
        console.log(`config key for ${key} ${filterCountry}:`, value);
      }
      return value;
    }

    if (filterCountry && filterCity) {
      this.lastCountry = filterCountry;
      this.lastCity = filterCity;

      // If an invalid city id is passed we should default to the region to prevent crashes
      let value = this.latestCountryConfig[key];
      if (this.latestCityConfig) {
        value = this.latestCityConfig[key];
      }
      if (__DEV__) {
        // If we had to default to the city we can show that message here
        console.log(`config key for ${key} ${filterCountry} ${this.latestCityConfig ? filterCity : 'invalid city found'}:`, value);
      }
      return value;
    }

    const value = currentContextConfig[key];
    if (__DEV__) {
      console.log(`config key for ${key}:`, value);
    }
    // If we haven't specified extra filters we can use the default context config
    return value;
  }

  constructor(ctx) {
    Object.defineProperty(this, 'ctx', {
      value: ctx,
      enumerable: false,
    });
  }

  /*
   * Trigger our config loading
   */
  setup() {
    // I know! in a browser!!?? Check the webpack file
    const {NODE_ENV} = process.env;

    const env = isServer ? NODE_ENV : window.ENV;
    this.loadConfig(env);
  }
}

export default Config;



// WEBPACK FOOTER //
// ./spa/state/config/index.jsx